import { useEffect, useRef, useState } from "react";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

export interface ChatMessage {
  role: "bot" | "me";
  text: string;
}

const AVATAR = 32;

const useStyles = makeStyles({
  log: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "16px",
    overflowY: "auto",
    maxHeight: "60vh",
    minHeight: "320px",
    // Always-visible thin scrollbar: overlay scrollbars hide that there's more above.
    scrollbarGutter: "stable",
    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.colorNeutralStroke1Pressed} transparent`,
    "::-webkit-scrollbar": { width: "10px" },
    "::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "::-webkit-scrollbar-thumb": { backgroundColor: tokens.colorNeutralStroke1Pressed, borderRadius: "8px", border: "3px solid transparent", backgroundClip: "content-box" },
    "::-webkit-scrollbar-thumb:hover": { backgroundColor: tokens.colorNeutralStrokeAccessible },
  },
  // A fixed-height window that scrolls inside the page.
  compact: { minHeight: "120px", maxHeight: "min(320px, 45vh)", paddingTop: "4px" },
  row: { display: "flex", alignItems: "flex-end", gap: "8px", animationName: { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "translateY(0)" } }, animationDuration: "220ms", animationTimingFunction: "ease-out" },
  rowMe: { justifyContent: "flex-end" },
  groupGap: { marginTop: "10px" },
  avatar: { width: `${AVATAR}px`, height: `${AVATAR}px`, flexShrink: 0, borderRadius: "50%", backgroundColor: tokens.colorNeutralBackground1, boxShadow: tokens.shadow4, padding: "3px", boxSizing: "border-box" },
  spacer: { width: `${AVATAR}px`, flexShrink: 0 },
  bubble: { maxWidth: "min(85%, 520px)", padding: "10px 14px", borderRadius: "18px", lineHeight: tokens.lineHeightBase300, fontSize: tokens.fontSizeBase300, whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
  bot: { backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground1, borderBottomLeftRadius: "6px" },
  me: { backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand, borderBottomRightRadius: "6px" },
  dots: { display: "inline-flex", gap: "4px", alignItems: "center", height: "20px", padding: "0 2px" },
  dot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralForeground3,
    animationName: { "0%, 80%, 100%": { opacity: 0.3, transform: "translateY(0)" }, "40%": { opacity: 1, transform: "translateY(-3px)" } },
    animationDuration: "1.1s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
  },
  caret: { display: "inline-block", width: "2px", height: "1em", marginLeft: "1px", verticalAlign: "-2px", backgroundColor: tokens.colorNeutralForeground3 },
});

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Chat transcript that "types": each new bot message gets a typing indicator, then its text appears
 * as if being typed. The trainer's own messages appear at once. `thinking` shows the indicator while
 * waiting for the server. `onSettled` fires when everything visible has finished typing.
 */
export function ChatLog({ messages, thinking, animateFrom, onSettled, compact = false }: { messages: ChatMessage[]; thinking: boolean; animateFrom: number; onSettled?: (settled: boolean) => void; compact?: boolean }) {
  const s = useStyles();
  const [shown, setShown] = useState(Math.min(animateFrom, messages.length));
  const [typing, setTyping] = useState<{ index: number; chars: number } | null>(null);
  const [dots, setDots] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const running = useRef(false);
  const mounted = useRef(true);
  const shownRef = useRef(shown);
  const msgsRef = useRef(messages);
  msgsRef.current = messages;
  useEffect(() => {
    mounted.current = true;
    return () => void (mounted.current = false);
  }, []);

  // Reveal queued messages one by one; one loop at a time, reading the latest messages each step.
  useEffect(() => {
    if (running.current || shownRef.current >= messages.length) return;
    running.current = true;
    const advance = (i: number) => {
      shownRef.current = i;
      setShown(i);
    };
    void (async () => {
      while (mounted.current && shownRef.current < msgsRef.current.length) {
        const i = shownRef.current;
        const m = msgsRef.current[i]!;
        if (m.role === "me" || reducedMotion()) {
          advance(i + 1);
          continue;
        }
        setDots(true);
        await sleep(Math.min(650, 280 + m.text.length * 4));
        setDots(false);
        const step = Math.max(1, Math.round(m.text.length / 22)); // ~22 frames (~0.35s) per message
        for (let c = step; c < m.text.length && mounted.current; c += step) {
          setTyping({ index: i, chars: c });
          await sleep(16);
        }
        setTyping(null);
        advance(i + 1);
        await sleep(140);
      }
      running.current = false;
    })();
  }, [messages.length]);

  const settled = shown >= messages.length && !typing && !dots;
  useEffect(() => onSettled?.(settled && !thinking), [settled, thinking, onSettled]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [shown, typing?.chars, dots, thinking]);

  const visible: { m: ChatMessage; text: string; live: boolean }[] = messages.slice(0, shown).map((m) => ({ m, text: m.text, live: false }));
  if (typing) visible.push({ m: messages[typing.index]!, text: messages[typing.index]!.text.slice(0, typing.chars), live: true });
  const showDots = dots || (thinking && settled);
  const lastRole = visible.at(-1)?.m.role;

  return (
    <div className={mergeClasses(s.log, compact && s.compact)} ref={logRef} aria-live="polite" aria-busy={!settled || thinking}>
      {visible.map(({ m, text, live }, i) => {
        const firstOfGroup = i === 0 || visible[i - 1]!.m.role !== m.role;
        const lastOfGroup = i === visible.length - 1 ? !(showDots && m.role === "bot") : visible[i + 1]!.m.role !== m.role;
        return (
          <div key={i} className={mergeClasses(s.row, m.role === "me" && s.rowMe, firstOfGroup && i > 0 && s.groupGap)}>
            {m.role === "bot" && (lastOfGroup ? <BotAvatar /> : <span className={s.spacer} />)}
            <div className={mergeClasses(s.bubble, m.role === "bot" ? s.bot : s.me)}>
              {text}
              {live && <span className={s.caret} aria-hidden />}
            </div>
          </div>
        );
      })}
      {showDots && (
        <div className={mergeClasses(s.row, lastRole !== "bot" && s.groupGap)} aria-label="RackUp is typing">
          <BotAvatar />
          <div className={mergeClasses(s.bubble, s.bot)}>
            <span className={s.dots}>
              <span className={s.dot} />
              <span className={s.dot} style={{ animationDelay: "0.15s" }} />
              <span className={s.dot} style={{ animationDelay: "0.3s" }} />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function BotAvatar() {
  const s = useStyles();
  return <img src="/icon-192.png" alt="RackUp" className={s.avatar} />;
}
